import { useUserStore } from 'hooks/stores/useUserStore';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { getEnterFlow2026Value } from 'utils/ab-test/abTests';
import { track_links } from 'utils/analytics/analytics';
import { ASSET_PREFIX, colors } from 'utils/constants';

import Footer2026, { FooterBackgroundColors } from './footer2026';
import { FooterLink } from './FooterLink';
import { PaymentLogoSection } from './PaymentLogoSection';
import { SignupNewsletter } from './SignupNewsletter';
import { SocialLinkSection } from './SocialLinkSection';
import { TrustIconSection } from './TrustIconSection';

const ControlFooter = () => {
  const { currentUser, subscriptions } = useUserStore();

  const faqLink =
    currentUser?.id &&
    subscriptions.find(
      (s) =>
        s.subscription.status === 'ACTIVE' ||
        s.subscription.status === 'SNOOZED' ||
        s.subscription.status === 'FAILING',
    )
      ? 'https://daymade.zendesk.com/hc/en-gb'
      : '/faq';

  useEffect(() => {
    track_links('#facebookext', 'Clicked Facebook link');
    track_links('#instagramext', 'Clicked Instagram link');
    track_links('#youtubeext', 'Clicked Youtube link');
    track_links('#tiktokext', 'Clicked Tiktok link');
    track_links('#faqlink', 'Clicked FAQ link');
    track_links('#contactuslink', 'Clicked Contact us link');
  }, []);

  return (
    <footer
      className="footer"
      style={{ backgroundColor: colors.footerBackground }}
    >
      <div className="footerContent grid">
        <div className="footerTopRow grid">
          <div className="logoColumn">
            <Link href="/">
              <img
                className="footerLogo"
                src={`${ASSET_PREFIX}/images/daymade-logo.png`}
                alt="DAYMADE logo"
              />
            </Link>
          </div>
          <div className="socialLinks">
            <SocialLinkSection />
          </div>
          <div className="paymentLogos">
            <PaymentLogoSection />
          </div>
        </div>

        <div className="separator footerSeparator" />

        <div className="footerLinksRow grid">
          <div className="footerLinkColumn">
            <FooterLink link={'/about-us'} text={'About Us'} />
            <FooterLink link={'/how-it-works'} text={'How It Works'} />
            <FooterLink text={'FAQ'} link={faqLink} id={'faqlink'} />
            <FooterLink
              link={'/contact-us'}
              text={'Contact Us'}
              id={'contactuslink'}
            />
          </div>
          <div className="footerLinkColumn">
            <FooterLink link={'/blog'} text={'Blog'} />
            <FooterLink link={'/responsible-play'} text={'Responsible Play'} />
            <FooterLink
              link={'/terms-and-conditions'}
              text={'Terms & Conditions'}
            />
            <FooterLink link={'/privacy-policy'} text={'Privacy Policy'} />

            {/* We need an extra element to balance the footer link columns on desktop devices. The footer links
            are spread evenly across the footer's height using flexbox, so we can align them to the top of the
            container*/}
            {/* <br className="extraFooterLink" /> */}
          </div>
          <div className="desktopNewsletter">
            <SignupNewsletter />
          </div>
        </div>
        <div className="separator mobileSeparator" />

        <div className="mobileNewsletter">
          <SignupNewsletter />
        </div>

        <div className="separator footerSeparator" />

        <div className="footerFullWidthRow grid">
          <TrustIconSection />
        </div>

        <div className="separator footerSeparator" />

        <div className="footerFullWidthRow grid">
          <div
            data-nosnippet
            style={{ fontWeight: 600, textAlign: 'center' }}
            className="text-centered"
          >
            DAYMADE is an associated brand of, and operated by, DAYMADE LTD
            (registered in England & Wales, company number 12333770).
            <br />
            <br />
            Please note that DAYMADE is an operator of prize draws. Our online
            contests to win prizes include a method of entry via post. No
            purchase is necessary to enter but the intention of the draws is
            that people enter to win prizes and support a worthy charitable
            cause.
          </div>
        </div>
      </div>
      <style jsx>
        {`
          .footer {
            width: 100%;
            padding: 17.5px;
            display: flex;
            justify-content: center;
          }

          .footerContent {
            width: 100%;
            max-width: 1200px;
            grid-template-columns: repeat(1, minmax(0, 1fr));
            gap: 28px;
            margin-top: 14px;
            margin-bottom: 14px;
          }

          .footerTopRow {
            width: 100%;
            grid-template-columns: repeat(1, minmax(0, 1fr));
            gap: 28px;
          }

          .logoColumn {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
          }

          .footerLogo {
            cursor: pointer;
            width: 150px;
          }

          .socialLinks {
            display: flex;
          }

          .paymentLogos {
            display: flex;
            justify-content: center;
          }

          .footerSeparator {
            margin: 0px !important;
            width: 100% !important;
          }

          .footerLinksRow {
            width: 100%;
            height: 100%;
            grid-template-columns: repeat(1, minmax(0, 1fr));
            gap: 14px;
          }

          .footerLinkColumn {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: 14px;
          }

          .desktopNewsletter {
            display: none;
            justify-content: center;
          }

          .mobileSeparator {
            display: flex;
            margin: 0px !important;
            width: 100% !important;
          }

          .mobileNewsletter {
            display: flex;
            justify-content: center;
          }

          .footerFullWidthRow {
            width: 100%;
            grid-template-columns: repeat(1, minmax(0, 1fr));
          }

          .extraFooterLink {
            display: none;
          }

          @media (min-width: 768px) {
            .footerTopRow {
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 0px;
            }

            .logoColumn {
              align-items: flex-start;
            }

            .footerLogo {
              width: 200px;
            }

            .paymentLogos {
              justify-content: flex-end;
            }

            .footerLinksRow {
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 0px;
            }

            .footerLinkColumn {
              align-items: flex-start;
            }

            .desktopNewsletter {
              display: flex;
              justify-content: flex-end;
            }

            .mobileSeparator {
              display: none;
            }

            .mobileNewsletter {
              display: none;
            }

            .extraFooterLink {
              display: block;
            }
          }
        `}
      </style>
    </footer>
  );
};

const Footer = ({ background }: { background?: FooterBackgroundColors }) => {
  const [abTest, setAbTest] = useState<
    ReturnType<typeof getEnterFlow2026Value> | undefined
  >(undefined);

  useEffect(() => {
    setAbTest(getEnterFlow2026Value());
  }, []);

  return (
    <>
      {abTest === undefined ? undefined : abTest !== 'CONTROL' ? (
        <Footer2026 background={background} />
      ) : (
        <ControlFooter />
      )}
    </>
  );
};

export default Footer;
